import type { MessageRule, PassCriterion, TestCase, TestSuite } from '@k-ucap/shared';

export const SELF_VALIDATION_SUITE_ID = 'k-ucap-self-validation-suite';

const now = () => new Date().toISOString();

const baseInertialRule = (overrides?: Partial<MessageRule> & { id: string }): MessageRule => ({
  id: `rule-101-${overrides?.id ?? 'base'}`,
  messageType: 101,
  messageDefinitionId: 'stanag4586-101-inertial-states',
  enabled: true,
  sourceRole: 'AIR_VEHICLE',
  targetRole: 'CUCS',
  payloadTemplate: { vehicleId: 'UAV-001', cucsId: 'CUCS-01' },
  fieldRules: {},
  generationMode: 'SINGLE',
  intervalMs: 0,
  count: 1,
  startDelayMs: 0,
  randomization: false,
  faultInjection: false,
  order: 0,
  ...overrides,
});

const basePassCriteria: PassCriterion[] = [
  { type: 'NO_FAIL', params: {} },
  { type: 'MIN_PASS_RATE', params: { minRate: 1 } },
];

const baseCase = (
  id: string,
  name: string,
  description: string,
  objective: string,
  messageRules: MessageRule[],
  passCriteria: PassCriterion[]
): TestCase => ({
  id,
  suiteId: SELF_VALIDATION_SUITE_ID,
  projectId: '',
  name,
  description,
  objective,
  preconditions: ['Project and user context are available'],
  messageRules,
  expectedResults: [description],
  passCriteria,
  timeoutMs: 30000,
  executionOrder: 0,
  enabled: true,
  tags: ['self-validation', 'builtin'],
  createdBy: '',
  createdAt: now(),
  updatedAt: now(),
});

export const buildSelfValidationSuite = (projectId: string, userId: string): TestSuite => ({
  id: SELF_VALIDATION_SUITE_ID,
  projectId,
  name: 'K-UCAP Self Validation Suite',
  description:
    'Built-in self-validation suite covering normal messages, negative scenarios, bulk generation, seed reproducibility, random coordinates, and replay.',
  category: 'Self-Validation',
  tags: ['builtin', 'self-validation'],
  version: '1.0.0',
  testCaseIds: [
    'svc-normal-message',
    'svc-missing-field',
    'svc-out-of-range',
    'svc-role-reverse',
    'svc-duplicate-sequence',
    'svc-timing-violation',
    'svc-bulk-1000',
    'svc-seed-reproducibility',
    'svc-random-coordinate',
    'svc-replay',
  ],
  createdBy: userId,
  createdAt: now(),
  updatedAt: now(),
});

export const buildSelfValidationCases = (suiteId: string, projectId: string, userId: string): TestCase[] => {
  const map = (caseTemplate: TestCase): TestCase => ({
    ...caseTemplate,
    suiteId,
    projectId,
    createdBy: userId,
  });

  const cases: TestCase[] = [
    map(
      baseCase(
        'svc-normal-message',
        'Normal message',
        'A valid Inertial States (#101) message should pass all checks.',
        'Verify that a correctly generated message evaluates to PASS.',
        [baseInertialRule({ id: 'normal' })],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 1, maxCount: 1 } },
          { type: 'EXPECTED_MESSAGE_TYPE', params: { messageType: 101 } },
          { type: 'ROLE_MATCH', params: { source: 'AIR_VEHICLE', target: 'CUCS' } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-missing-field',
        'Missing required field',
        'Removing the required latitude field should produce a FAIL or WARN.',
        'Verify that validation catches a missing required field.',
        [
          baseInertialRule({
            id: 'missing-field',
            fieldRules: {
              latitude: { type: 'FIXED', value: undefined },
            },
          }),
        ],
        [...basePassCriteria, { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 1, maxCount: 1 } }]
      )
    ),
    map(
      baseCase(
        'svc-out-of-range',
        'Out of range field',
        'A latitude value above the maximum allowed range should produce a FAIL.',
        'Verify that validation catches a numeric field outside its defined range.',
        [
          baseInertialRule({
            id: 'out-of-range',
            fieldRules: {
              latitude: { type: 'FIXED', value: 95 },
            },
          }),
        ],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 1, maxCount: 1 } },
          { type: 'FIELD_RANGE', params: { field: 'latitude', min: -90, max: 90 } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-role-reverse',
        'Role reverse',
        'Swapping source and target roles should produce a FAIL.',
        'Verify that role mismatch is detected when source/target are reversed.',
        [
          baseInertialRule({
            id: 'role-reverse',
            sourceRole: 'CUCS',
            targetRole: 'AIR_VEHICLE',
          }),
        ],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 1, maxCount: 1 } },
          { type: 'ROLE_MATCH', params: { source: 'AIR_VEHICLE', target: 'CUCS' } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-duplicate-sequence',
        'Duplicate sequence',
        'Two messages with the same sequence number should produce a FAIL.',
        'Verify that duplicate sequence numbers are detected.',
        [baseInertialRule({ id: 'duplicate-a' }), baseInertialRule({ id: 'duplicate-b' })],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 2, maxCount: 2 } },
          { type: 'CUSTOM_EXPRESSION', params: { expression: 'NO_DUPLICATE_SEQUENCE' } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-timing-violation',
        'Timing violation',
        'Messages spaced farther apart than allowed should produce a FAIL.',
        'Verify that the timing criterion detects an interval violation.',
        [
          baseInertialRule({
            id: 'timing',
            generationMode: 'PERIODIC',
            count: 2,
            intervalMs: 0,
          }),
        ],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 2, maxCount: 2 } },
          { type: 'TIMING_WITHIN', params: { maxIntervalMs: 200 } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-bulk-1000',
        'Bulk 1,000 messages',
        '1,000 Inertial States messages generated in bulk should PASS.',
        'Verify that bulk generation completes without failures and produces the expected count.',
        [
          baseInertialRule({
            id: 'bulk',
            generationMode: 'BULK',
            count: 1000,
          }),
        ],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 1000, maxCount: 1000 } },
          { type: 'EXPECTED_MESSAGE_TYPE', params: { messageType: 101 } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-seed-reproducibility',
        'Seed reproducibility',
        'Two runs with the same seed should produce identical messages.',
        'Verify deterministic generation when a seed is fixed.',
        [
          baseInertialRule({
            id: 'seed',
            generationMode: 'BULK',
            count: 10,
            randomization: true,
          }),
        ],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 10, maxCount: 10 } },
          { type: 'EXPECTED_MESSAGE_TYPE', params: { messageType: 101 } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-random-coordinate',
        'Random coordinate',
        'Random latitude/longitude generation should PASS and keep the pair atomic.',
        'Verify that a generated coordinate pair is consistent and within valid ranges.',
        [
          baseInertialRule({
            id: 'random-coord',
            randomization: true,
            fieldRules: {
              latitude: { type: 'RANDOM_COORDINATE' },
              longitude: { type: 'RANDOM_COORDINATE' },
            },
          }),
        ],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 1, maxCount: 1 } },
          { type: 'EXPECTED_MESSAGE_TYPE', params: { messageType: 101 } },
          { type: 'FIELD_RANGE', params: { field: 'latitude', min: -90, max: 90 } },
          { type: 'FIELD_RANGE', params: { field: 'longitude', min: -180, max: 180 } },
          { type: 'CUSTOM_EXPRESSION', params: { expression: 'RANDOM_COORDINATE_ATOMICITY' } },
        ]
      )
    ),
    map(
      baseCase(
        'svc-replay',
        'Replay',
        'A single replay-style Inertial States message should PASS.',
        'Verify that a deterministic replay message is accepted.',
        [
          baseInertialRule({
            id: 'replay',
            payloadTemplate: {
              vehicleId: 'REPLAY-01',
              cucsId: 'CUCS-01',
            },
          }),
        ],
        [
          ...basePassCriteria,
          { type: 'EXPECTED_MESSAGE_COUNT', params: { minCount: 1, maxCount: 1 } },
          { type: 'EXPECTED_MESSAGE_TYPE', params: { messageType: 101 } },
        ]
      )
    ),
  ];

  return cases.map((c, index) => ({ ...c, executionOrder: index }));
};
